import { api } from './client';
import type {
  GalleryFilters,
  ImageDetail,
  ImageRatings,
  ImageFavorites,
  ImageSummary,
} from './types';

interface ListImagesResponse {
  total?: number;
  images?: ImageSummary[];
}

function normalizeImage<T extends ImageSummary>(img: T): T {
  return {
    ...img,
    takenAt: img.takenAt ?? img.taken_at ?? null,
    commentCount: img.commentCount ?? img.comments_count ?? 0,
    ratings: img.ratings ?? {},
    favorites: img.favorites ?? {},
  };
}

// Mappar våra UI-filter till query-params som backend förstår.
// Backend stödjer: min_stars, max_stars, stars (komma-sep), stars_user, favorite.
// Vi använder min_stars (intervallform) för "X+ stjärnor" och stars (exakt)
// för "exakt 5 stjärnor".
function buildQuery(filters: GalleryFilters | undefined) {
  if (!filters) return {} as Record<string, string | number | undefined>;
  const q: Record<string, string | number | undefined> = {};

  switch (filters.stars) {
    case '1+': q.min_stars = 1; break;
    case '2+': q.min_stars = 2; break;
    case '3+': q.min_stars = 3; break;
    case '4+': q.min_stars = 4; break;
    case '5':  q.min_stars = 5; break;
    case 'all':
    default: break;
  }

  // user='all' → "any" (OR mellan henrik/michelle), 'both' → AND, övriga = exakt person.
  // Backend stödjer både stars_user= och favorite= med samma värdesymbolik.
  const userParam = filters.user === 'all' ? 'any' : filters.user;
  if (filters.user !== 'all') {
    q.stars_user = userParam;
  }
  if (filters.favorites === 'on') {
    // Backend-param heter `favorite`, ej `favorited_by`.
    q.favorite = userParam;
  }

  return q;
}

export async function listImages(
  shootId: string,
  filters?: GalleryFilters,
): Promise<ImageSummary[]> {
  // Backend defaultar till 200 bilder per anrop. För Loupe-galleriet vill vi
  // ha hela shooten i en enda fetch (även 500+ bilder är OK för 2 användare).
  // Vi pagerar genom resultaten själva tills vi har allt.
  const PAGE_SIZE = 500; // matchar backend max
  const baseQuery = buildQuery(filters);
  const all: ImageSummary[] = [];
  let offset = 0;

  while (true) {
    const data = await api.get<ImageSummary[] | ListImagesResponse>(
      `/shoots/${shootId}/images`,
      { query: { ...baseQuery, limit: PAGE_SIZE, offset } },
    );
    const page = Array.isArray(data) ? data : data.images ?? [];
    const total = !Array.isArray(data) ? data.total : undefined;
    all.push(...page.map(normalizeImage));
    // Slut: backend gav färre än begärt, eller vi har nått total.
    if (page.length < PAGE_SIZE) break;
    if (total != null && all.length >= total) break;
    offset += PAGE_SIZE;
    // Säkerhetsbroms — orimligt stort galleri tyder på en bug.
    if (offset > 10000) break;
  }
  return all;
}

export async function getImage(imageId: string): Promise<ImageDetail> {
  const data = await api.get<ImageDetail | { image: ImageDetail }>(
    `/images/${imageId}`,
  );
  // Backend wrappar i { image: {...} } — toleranta för båda formerna.
  const img = (data && typeof data === 'object' && 'image' in data
    ? (data as { image: ImageDetail }).image
    : (data as ImageDetail));
  return normalizeImage(img);
}

// Sätter den inloggade användarens betyg (0–5). Backend bestämmer "vem" via
// session-cookien, vi skickar bara stars-värdet.
export async function setRating(
  imageId: string,
  stars: number,
): Promise<{ ratings: ImageRatings }> {
  return api.put<{ ratings: ImageRatings }>(`/images/${imageId}/rating`, {
    stars,
  });
}

// Sätter favoritmarkering explicit till true/false. (Vi använder PUT så att
// frontend kan undvika race-condition om snabba dubbel-klick.)
export async function setFavorite(
  imageId: string,
  favorite: boolean,
): Promise<{ favorites: ImageFavorites }> {
  return api.put<{ favorites: ImageFavorites }>(
    `/images/${imageId}/favorite`,
    { favorite },
  );
}
